import {
  createRoute,
  formatDifferenceInHoursAndMinutes,
  formatTime,
  INFO_NOT_AVAILABLE_NA,
} from '@backoffice-frontend/common'
import {
  MoiaGrid,
  MoiaIconLabel,
  useTheme,
} from '@backoffice-frontend/patterns'

import { StopNetworkAreaRoutes } from '@backoffice-frontend/sam-common'
import { isPast } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { VEHICLE_MISSION_WAYPOINT_DESTINATION_HEIGHT } from '../../constants'
import type { VehicleMissionFccUpcomingWaypoint } from '../../parseVehicle'

type Waypoint = Pick<VehicleMissionFccUpcomingWaypoint, 'stop' | 'actions'>

const FLEET_API_STOP_ID = 'fleet-api-stop'

export const VehicleMissionWaypointDestination = ({
  waypoint,
  waypointTypes,
}: {
  waypoint: Waypoint
  waypointTypes: {
    icon?: React.ReactNode
  }[]
}) => {
  const theme = useTheme()
  const { i18n } = useTranslation()

  const waypointType = waypointTypes.at(0)
  if (!waypointType) {
    return null
  }

  const { icon } = waypointType

  const latestArrivingWaypointAction = waypoint.actions.reduce(
    (latest, action) => {
      if (!isPast(new Date(action.estimatedAt))) return action
      return latest
    },
  )
  const { estimatedAt: latestArrivingWaypointAt } = latestArrivingWaypointAction

  const latestArrivingWaypointTripDestinationPromisedTime =
    latestArrivingWaypointAction?.promisedTimeRange.to

  const latestArrivingWaypointIsPast = isPast(
    new Date(latestArrivingWaypointAt ?? ''),
  )

  const waypointStopLink =
    waypoint.stop &&
    waypoint.stop.id !== FLEET_API_STOP_ID &&
    createRoute(StopNetworkAreaRoutes.Show.Stops.Show.route, {
      serviceAreaId: waypoint.stop.serviceArea.id,
      stopId: waypoint.stop.id,
    })

  const firstWaypointAction = waypoint.actions.at(0)
  const waypointAreaLink =
    firstWaypointAction?.__typename === 'FccWaypointHubReturnAction' &&
    firstWaypointAction.area &&
    createRoute(StopNetworkAreaRoutes.Show.Areas.Show.route, {
      serviceAreaId: firstWaypointAction.area.serviceArea.id,
      areaId: firstWaypointAction.area.id,
    })

  const waypointStopName =
    waypoint.stop?.names[i18n.language] ?? INFO_NOT_AVAILABLE_NA

  return (
    <MoiaGrid
      gridGap={0}
      gridTemplateColumns={`${theme.space.Space4} 56px 4fr 1fr ${theme.space.Space4}`}
      gridTemplateRows={`${VEHICLE_MISSION_WAYPOINT_DESTINATION_HEIGHT}px`}
      alignItems="center"
      css={{
        position: 'relative',
      }}
    >
      <MoiaIconLabel
        css={theme => ({
          gridColumnStart: 3,
          justifyContent: 'start',
          color: theme.semantic.ColorActionSecondaryDefault,
          overflow: 'hidden',
          gridTemplateColumns: 'min-content minmax(0, 1fr)',
          svg: {
            width: 13,
            flexShrink: 0,
          },
        })}
      >
        {icon}
        {waypointStopLink ? (
          <Link
            type="primary"
            target="_blank"
            to={waypointStopLink}
            css={{
              display: 'block',
              color: 'currentcolor',
              textDecoration: 'none',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
            }}
          >
            {waypointStopName}
          </Link>
        ) : waypointAreaLink ? (
          <Link
            target="_blank"
            to={waypointAreaLink}
            css={{
              display: 'block',
              color: 'currentcolor',
              textDecoration: 'none',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
            }}
          >
            {firstWaypointAction.area?.name}
          </Link>
        ) : (
          INFO_NOT_AVAILABLE_NA
        )}
      </MoiaIconLabel>
      {latestArrivingWaypointAction && (
        <span
          css={{
            alignItems: 'baseline',
            color: theme.semantic.ColorContentDefault,
            display: 'flex',
            marginLeft: 'auto',
          }}
        >
          {latestArrivingWaypointAt && (
            <span css={{ fontSize: 14, paddingLeft: 3 }}>
              {!latestArrivingWaypointIsPast
                ? formatTime(new Date(latestArrivingWaypointAt))
                : latestArrivingWaypointTripDestinationPromisedTime &&
                  formatTime(
                    new Date(latestArrivingWaypointTripDestinationPromisedTime),
                  )}
            </span>
          )}
          {latestArrivingWaypointTripDestinationPromisedTime &&
            isPast(
              new Date(latestArrivingWaypointTripDestinationPromisedTime),
            ) && (
              <span
                css={{
                  color: theme.semantic.ColorCriticalDefault,
                  fontWeight: 700,
                  paddingLeft: theme.space.Space2,
                }}
              >
                +
                {formatDifferenceInHoursAndMinutes(
                  new Date(
                    latestArrivingWaypointTripDestinationPromisedTime ?? '',
                  ),
                  new Date(),
                )}
              </span>
            )}
        </span>
      )}
    </MoiaGrid>
  )
}
